import React, {FC, useState} from 'react'
import moment from 'moment'
import {Dropdown, OverlayTrigger, Tooltip} from 'react-bootstrap'
import styles from './ConversationCard.module.scss'
import {ConversationData, TeamMember} from '../../types/inbox-v3.types'

interface ConversationCardProps {
  conversation: ConversationData
  isSelected: boolean
  isActive: boolean
  isBulkActionsActive?: boolean
  teamMembers?: TeamMember[]
  onSelect: (id: string, selected: boolean) => void
  onClick: () => void
  onArchive: (id: string) => void
  onFlag: (id: string) => void
  onAssign?: (id: string, assignee: TeamMember) => void
  onTag?: (id: string) => void
  onUnsubscribe?: (id: string) => void
  onSubscribe?: (id: string) => void
}

const ConversationCard: FC<ConversationCardProps> = ({
  conversation,
  isSelected,
  isActive,
  isBulkActionsActive = false,
  teamMembers = [],
  onSelect,
  onClick,
  onArchive,
  onFlag,
  onAssign,
  onTag,
  onUnsubscribe,
  onSubscribe,
}) => {
  const {_id, receiver, message, createdAt, contacts, contactgroupmaps} = conversation
  const {isInboxRead, isArchived, isFlagged, isSubscribed} = contacts
  const {fields} = contactgroupmaps

  // Get display name
  const firstName = fields.find((f) => f.name === 'First Name')?.value || ''
  const lastName = fields.find((f) => f.name === 'Last Name')?.value || ''
  const displayName = `${firstName} ${lastName}`.trim() || receiver

  // Get initials for avatar
  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase()
    }
    // For phone number, use last 2 digits
    return receiver.slice(-2)
  }

  // Truncate message
  const truncateMessage = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  // Format time
  const formatTime = (dateStr: string) => {
    const date = moment(dateStr)
    const now = moment()

    if (date.isSame(now, 'day')) {
      return date.format('h:mm A')
    } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
      return 'Yesterday'
    } else if (date.isSame(now, 'week')) {
      return date.format('ddd')
    } else {
      return date.format('DD/MM')
    }
  }

  // Check if unread
  const isUnread = !isInboxRead
  // Check if read (for green line indicator) - mutually exclusive with unread
  const isRead = isInboxRead && !isActive && !isSelected && !isArchived

  // Build class names
  const cardClasses = [
    styles.conversationCard,
    isSelected && styles.selected,
    isActive && styles.active,
    isUnread && styles.unread,
    isRead && styles.read, // Green line for read messages (when not active/selected/archived)
    isFlagged && styles.flagged,
    isArchived && styles.archived, // Archived state styling
  ]
    .filter(Boolean)
    .join(' ')

  // Handle action button clicks
  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onArchive(_id)
  }

  const handleFlagClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onFlag(_id)
  }

  // Tooltip component
  const actionTooltip = (text: string) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s+/g, '-')}`}>{text}</Tooltip>
  )

  return (
    <div className={cardClasses} onClick={onClick}>
      {/* Unread indicator container - always rendered for alignment */}
      <div className={styles.unreadDotContainer}>
        {isUnread && <div className={styles.unreadDot}></div>}
      </div>

      {/* Avatar */}
      <div className={styles.avatar}>{getInitials()}</div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.name}>{displayName}</div>
        <div className={styles.messageRow}>
          <div className={styles.message}>{truncateMessage(message, 200)}</div>
          <span className={styles.time}>{formatTime(createdAt)}</span>
        </div>
      </div>

      {/* Actions - Separate element positioned top-right */}
      <div className={styles.actionsContainer}>
        <div className={styles.actionsRow}>
          {/* Hover Actions (Archive and Flag) - Flag always visible when active */}
          <div className={`${styles.hoverActions} ${isFlagged ? styles.showFlag : ''}`}>
            <OverlayTrigger
              placement='top'
              overlay={actionTooltip(isArchived ? 'Unarchive' : 'Archive')}
            >
              <button
                className={`${styles.actionButton} ${styles.archiveButton} ${
                  isArchived ? styles.archived : ''
                }`}
                onClick={handleArchiveClick}
              >
                <i className={`bi ${isArchived ? 'bi-archive-fill' : 'bi-archive'}`}></i>
              </button>
            </OverlayTrigger>

            <OverlayTrigger placement='top' overlay={actionTooltip(isFlagged ? 'Unflag' : 'Flag')}>
              <button
                className={`${styles.actionButton} ${styles.flagButton} ${
                  isFlagged ? styles.flagged : ''
                }`}
                onClick={handleFlagClick}
              >
                <i className={`bi ${isFlagged ? 'bi-flag-fill text-danger' : 'bi-flag'}`}></i>
              </button>
            </OverlayTrigger>
          </div>

          {/* Three-dot menu - always visible */}
          <Dropdown
            className={styles.moreActionsDropdown}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Dropdown.Toggle as='button' className={styles.moreActionsButton} bsPrefix='custom'>
              <i className='bi bi-three-dots-vertical'></i>
            </Dropdown.Toggle>
            <Dropdown.Menu
              align='end'
              className={styles.moreActionsMenu}
              renderOnMount
              popperConfig={{
                strategy: 'fixed',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                ],
              }}
            >
              {onAssign && teamMembers.length > 0 && (
                <Dropdown as='div' drop='start'>
                  <Dropdown.Toggle as='div' className={styles.menuItem} style={{cursor: 'pointer'}}>
                    <i className='bi bi-person-plus me-2'></i>Assign
                    <i className='bi bi-chevron-right ms-auto'></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{
                      strategy: 'fixed',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                          },
                        },
                      ],
                    }}
                  >
                    {teamMembers.map((member) => (
                      <Dropdown.Item key={member._id} onClick={() => onAssign(_id, member)}>
                        {member.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {onTag && (
                <Dropdown.Item onClick={() => onTag(_id)} className={styles.menuItem}>
                  <i className='bi bi-tag me-2'></i>Add Tag
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={handleArchiveClick} className={styles.menuItem}>
                <i className={`bi ${isArchived ? 'bi-archive-fill' : 'bi-archive'} me-2`}></i>
                {isArchived ? 'Unarchive' : 'Archive'}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleFlagClick}
                className={`${styles.menuItem} ${isFlagged ? styles.flaggedItem : ''}`}
              >
                <i className={`bi ${isFlagged ? 'bi-flag-fill' : 'bi-flag'} me-2`}></i>
                {isFlagged ? 'Unflag' : 'Flag'}
              </Dropdown.Item>
              {/* Subscribe/Unsubscribe based on current status */}
              {isSubscribed === false && onSubscribe && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => onSubscribe(_id)}
                    className={`${styles.menuItem} text-success`}
                  >
                    <i className='bi bi-check-circle me-2'></i>Subscribe
                  </Dropdown.Item>
                </>
              )}
              {isSubscribed !== false && onUnsubscribe && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => onUnsubscribe(_id)}
                    className={`${styles.menuItem} text-danger`}
                  >
                    <i className='bi bi-x-circle me-2'></i>Unsubscribe
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  )
}

export default ConversationCard